import { useMemo, useState } from "react";

const ReportCard = ({ data = [] }) => {
  const [selectedDate, setSelectedDate] = useState("");

  // ✅ Filter data by selected date based on createdAt
  const filteredData = useMemo(() => {
    if (!selectedDate) return data;
    return data.filter((item) => {
      if (!item.createdAt) return false;
      const itemDate = new Date(item.createdAt).toISOString().slice(0, 10);
      return itemDate === selectedDate;
    });
  }, [data, selectedDate]);

  // ✅ Define only Zone 2 and Zone 4
  const predefinedZones = ["zone2", "zone4"];

  // ✅ Calculate totals and group by zone
  const report = useMemo(() => {
    if (!filteredData.length) return null;

    // Total payment
    const totalPayment = filteredData.reduce(
      (sum, item) => sum + (parseFloat(item.payment) || 0),
      0
    );

    // Unique units overall
    const uniqueUnitsOverall = new Set(
      filteredData.map((item) => item.unitNumber)
    ).size;

    // Group by zoneId
    const zoneMap = {};
    filteredData.forEach((item) => {
      const zone = item.zoneId || "unknown";
      const payment = parseFloat(item.payment) || 0;
      const unit = item.unitNumber;

      if (!zoneMap[zone]) {
        zoneMap[zone] = { payment: 0, units: new Set() };
      }

      zoneMap[zone].payment += payment;
      zoneMap[zone].units.add(unit);
    });

    // Build display array for predefined zones
    const zones = predefinedZones.map((zone) => {
      const info = zoneMap[zone] || { payment: 0, units: new Set() };
      const payment = info.payment;
      const percentage = totalPayment
        ? ((payment / totalPayment) * 100).toFixed(2)
        : "0.00";

      return {
        zone: zone.toUpperCase(), // Show as ZONE2 / ZONE4
        payment,
        percentage,
        units: info.units.size,
      };
    });

    return {
      totalPayment,
      uniqueUnits: uniqueUnitsOverall,
      zones,
    };
  }, [filteredData]);

  return (
    <div className="p-5 rounded-md border bg-white shadow-md w-full max-w-md">
      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold uppercase text-gray-800">
          Report
        </h2>
        <input
          type="date"
          value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
          className="border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring focus:ring-blue-300"
        />
      </div>

      {report ? (
        <div className="text-gray-700 space-y-2">
          <p className="font-semibold text-lg">Jumlah Bayaran</p>

          <p className="text-base">
            Jumlah:{" "}
            <span className="font-medium text-blue-700">
              RM{" "}
              {report.totalPayment.toLocaleString(undefined, {
                minimumFractionDigits: 2,
              })}
            </span>
          </p>

          <p className="text-base">
            Jumlah Unit:{" "}
            <span className="font-medium text-blue-700">
              {report.uniqueUnits} Unit
            </span>
          </p>

          <hr className="my-3 border-gray-300" />

          <div className="space-y-1">
            {report.zones.map((z) => (
              <p key={z.zone}>
                {z.zone}:{" "}
                <span
                  className={
                    z.payment > 0
                      ? "font-medium text-green-700"
                      : "font-medium text-gray-500"
                  }
                >
                  RM{" "}
                  {z.payment.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                  })}
                </span>{" "}
                | <span className="text-gray-600">{z.percentage}%</span> |{" "}
                <span className="text-gray-600">{z.units} Unit(s)</span>
              </p>
            ))}
          </div>
        </div>
      ) : (
        <p className="text-gray-500 text-sm">
          No data available for selected date
        </p>
      )}
    </div>
  );
};

export default ReportCard;
